import loadADIInvoices, { invoiceParams } from "@NA/Handlers/InvoiceHistory/ADIInvoiceHistoryHandler";
import loadADIMultipleInvoices from "@NA/Handlers/InvoiceHistory/ADIMultipleInvoicesHandler";
import CustomApplicationState from "@NA/Store/Data/CustomApplicationState/CustomApplicationState";
import ADIColors from "@NA/Utils/ADIColors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import updateSearchFields from "@insite/client-framework/Store/Pages/InvoiceHistory/Handlers/UpdateSearchFields";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import { getProp } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getColor from "@insite/mobius/utilities/getColor";
import * as React from "react";
import { connect, ResolveThunks, useDispatch } from "react-redux";
import { css } from "styled-components";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";

const mapStateToProps = (state: CustomApplicationState) => ({
    getInvoicesParameter: state.ADIGlobalData.ADIInvoiceStore.loadInvoiceParameters,
    invoiceResults: state.ADIGlobalData.ADIInvoiceStore.invoiceResults,
    billToState: getBillToState(state, state.context.session.billToId),
    currentInvoiceTab: state.ADIGlobalData.ADIInvoiceStore.currentInvoiceTab,
});

const mapDispatchToProps = {
    loadADIInvoices,
    loadADIMultipleInvoices,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface InvoiceHeaderTabsStyles {
    container?: InjectableCss;
    tabs?: InjectableCss;
}

export const tableStyles: InvoiceHeaderTabsStyles = {
    container: {
        css: css`
            display: flex;
            margin: 24px 0;
            padding: 2px;
            border-radius: 6px;
            border: solid 1px ${ADIColors.gray2};
            background: ${ADIColors.white};
            /* max-width: 599px; */
        `,
    },
    tabs: {
        css: css`
            padding: 6.5px 10px 5.5px 10px;
            border-radius: 6px;
            border: 0;
            background-color: #fff;
            text-align: center;
            font-size: ${getProp("theme.typography.headerTertiary.size")};
            line-height: 22px;
            font-weight: normal;
            display: flex;
            width: calc(100% / 3);
            justify-content: center;
            align-items: center;
            cursor: pointer;
            transition: all 0.1s ease;
            position: relative;
            overflow: visible;

            &:nth-of-type(2) {
                margin: 0 9px;
            }

            &::after {
                content: "";
                width: 3px;
                height: 22px;
                margin: 6px 0 6px 1px;
                border-radius: 1px;
                background-color: ${ADIColors.gray2};
                position: absolute;
                right: -6px;
                z-index: 10;
            }

            &:last-child::after {
                display: none;
            }

            &.active {
                background-color: ${ADIColors.darkBlue1};
                color: ${ADIColors.white};
            }
        `,
    },
};

const styles = tableStyles;

const InvoiceHeaderTabs = ({
    getInvoicesParameter,
    invoiceResults,
    loadADIInvoices,
    loadADIMultipleInvoices,
    billToState,
    currentInvoiceTab,
}: Props) => {
    const [current, setCurrent] = React.useState(currentInvoiceTab);
    const customerInfo = invoiceResults?.properties;
    const CustomerNumber = billToState.value?.customerNumber;
    const dispatch = useDispatch();

    if (!invoiceResults?.properties) {
        return null;
    }

    const updateInvoiceTabStatus = (tab: number) => {
        dispatch({
            type: "Custom/TabStatusUpdate",
            result: tab,
        });
    };

    const updateTab = (num: number = 0) => {
        if (current === num) {
            return;
        }

        updateInvoiceTabStatus(num);

        setCurrent(num);
        if (num === 0) {
            const invoiceContainer = document.querySelector(".DESC");
            const invoiceContainer2 = document.querySelector(".non-selected");

            if (invoiceContainer) {
                invoiceContainer.classList.add("viewalllocationinvoice");
            }

            if (invoiceContainer2) {
                invoiceContainer2.parentElement?.classList.add("viewalllocationinvoiceinside");
            }
            loadADIInvoices({
                ...invoiceParams,
                ...getInvoicesParameter,
                customerSequence: "-1",
            });
        }

        if (num === 1) {
            const invoiceContainer = document.querySelector(".DESC");
            const invoiceContainer2 = document.querySelector(".non-selected");

            if (invoiceContainer) {
                invoiceContainer.classList.remove("viewalllocationinvoice");
            }

            if (invoiceContainer2) {
                invoiceContainer2.parentElement?.classList.remove("viewalllocationinvoiceinside");
            }
            loadADIMultipleInvoices({
                ...invoiceParams,
                customerSequence: "ALL",
            });
        }

        if (num === 2) {
            const invoiceContainer = document.querySelector(".DESC");
            const invoiceContainer2 = document.querySelector(".non-selected");

            const invoiceContainer3 = document.querySelector(".ASC.viewalllocationinvoiceasc");
            if (invoiceContainer3) {
                invoiceContainer3.classList.remove("viewalllocationinvoiceasc");
            }

            if (invoiceContainer) {
                invoiceContainer.classList.remove("viewalllocationinvoice");
            }

            if (invoiceContainer2) {
                invoiceContainer2.parentElement?.classList.remove("viewalllocationinvoiceinside");
            }
            loadADIInvoices({
                ...invoiceParams,
                ...getInvoicesParameter,
                customerSequence: "ALLInv",
            });
        }
    };

    return (
        <StyledWrapper {...styles.container}>
            <StyledWrapper
                {...styles.tabs}
                className={current === 0 ? "active" : "normal"}
                onClick={() => updateTab(0)}
            >
                {/* {translate("View {0}", `${customerInfoTemp?.custNumber}-${customerInfoTemp?.suffix}`)} */}
                {translate("View {0}", `${CustomerNumber}`)}
            </StyledWrapper>
            <StyledWrapper
                {...styles.tabs}
                className={current === 1 ? "active" : "normal"}
                onClick={() => updateTab(1)}
            >
                {translate("View All Locations")}
            </StyledWrapper>
            <StyledWrapper
                {...styles.tabs}
                className={current === 2 ? "active" : "normal"}
                onClick={() => updateTab(2)}
            >
                {translate("View All Invoices")}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(InvoiceHeaderTabs),
    definition: {
        group: "Invoice History",
        displayName: "InvoiceHeader Tabs",
    },
};

export default widgetModule;
